/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { urls } from "../urls";
import { extendViewing, transform } from "utils";
import { generateQueryParams } from "utils/queryParams/transaction";
import { QueryRequestBody } from "interfaces/common/QueryRequestBody"
import { SearchParams } from "customTypes/SearchParam";
import { useTableStore, useUserStore } from "../state";
import { TableNames } from "customEnums/TableNames";
import { Config, PageNames } from "interfaces/config"
import { useAuth } from "./useAuth";
import { handleUnauthorized } from "utils/handleUnauthorized";
import { transactionSummaryStore } from "state/TransactionSummaryStore";
import { useStore } from "zustand";

let localizedFormat = require('dayjs/plugin/localizedFormat')
let timezone = require('dayjs/plugin/timezone')

dayjs.extend(localizedFormat)
let utc = require('dayjs/plugin/utc')
dayjs.extend(utc)
dayjs.extend(timezone)

export const useQueryTransactions = (
    pageSize: number,
    currentPage: number,
    userRole: any,
    searchParams: SearchParams,
    auditSearch: boolean
) => {

    const url = urls.QUERY;
    const [loading, setLoading] = useState(false);
    const [mappedData, setMappedData] = useState<any>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [error, setError] = useState<any>();
    const columnState = useTableStore(state => state.columnState)
    const pagination = useTableStore(state => state.pagination)
    const runFilters = useTableStore(state => state.runFilters)
    const setRunFilters = useTableStore(state => state.setRunFilters)

    const userConfig = useUserStore(state => state.userConfig)
    const config: Config = useUserStore(state => state.config)
    const { softLimit, hardLimit } = config.transactionViewing
    const { userRoles } = config.cognito
    const { isAdmin } = userConfig;
    const pageRef = useRef(currentPage)
    const runCount = useRef(0);
    const { logout } = useAuth();
    const tableQueryAbortController = useStore(transactionSummaryStore, (state: any) => state.tableQueryAbortController)
    const setTableQueryAbortController = useStore(transactionSummaryStore, (state: any) => state.setTableQueryAbortController)
    const setLoadingSearchResults = useStore(transactionSummaryStore, (state: any) => state.setLoadingSearchResults)
    const { order, orderBy } = pagination[TableNames.TRANSACTION_SUMMARY]
    const tableName = useTableStore(state => state.pageName);

    let filters = columnState.page["transaction-summary"]["transaction-summary"]?.filters
    //merge with activeFilters from userCofnig

    const filterLabels: string[] = [];
    if (filters) {
        filters.forEach((filter) => {
            let filterValueKeys = Object.keys(filter.filterValues)
            filterValueKeys.forEach((key) => {
                if (filter.filterValues[key].selected === true) {
                    filterLabels.push(filter.filterValues[key].label)
                }
            })
        })
    }

    useEffect(() => {
        runCount.current = runCount.current + 1
        if (tableName !== 'transaction-summary') {
            console.log('useQueryTransactions: Not on transaction-summary page, skipping query');
            return
        }
        console.log('debug stuck search results useQueryTransactions: ', { runCount: runCount.current, searchParams })
        const controller = new AbortController();
        const { signal } = controller
        const param = searchParams.find((param) => param.column === "transactionDate" || param.column === "lowerDate" || param.column === "upperDate" || param.column === "dob")

        if (userRole && filterLabels.length > 0 && softLimit && userRoles.length > 0 && isAdmin) {

            const queryParams = generateQueryParams(softLimit.toString(), PageNames.TRANSACTION_SUMMARY, userConfig, hardLimit, isAdmin, searchParams, filterLabels, orderBy, order, param)

            let queryDataRequest: QueryRequestBody = {
                "queries": [
                    {
                        "queryParams": [
                            ...queryParams,
                            {
                                "value": order,
                                "name": "order"
                            }
                        ],
                        "limit": pageSize,
                        "offset": (currentPage - 1) * pageSize,
                        "queryName": process.env.REACT_APP_TRANSACTION_TABLE_QUERY!,
                        // will have a check here until we deploy new UI to NM prod
                        "transformData": process.env.REACT_APP_TRANSFORM_DATA === "true" ? true : false
                        ,
                    }
                ]
            }
            const fetchData = async (queryDataRequest: QueryRequestBody) => {
                let runSearch = false
                const searchParamsKeys = searchParams.length > 0 ? Object.keys(searchParams[0]) : []

                if (searchParamsKeys.length > 0) {
                    runSearch = searchParams.some((param: any) => param?.hasOwnProperty("value"))
                } else {
                    runSearch = true
                }

                if (runSearch) {
                    setTableQueryAbortController(controller)

                    if (tableQueryAbortController) {
                        tableQueryAbortController.abort()
                    }

                    try {
                        setLoading(true)
                        const transactionalUrl: string = `${process.env.NODE_ENV === "production" ? url : ""}/query`;

                        const getData = async () => {
                            const data = await (await fetch(transactionalUrl, {
                                method: "POST",
                                credentials: "include",
                                body: JSON.stringify(queryDataRequest),
                                signal
                            })).json();
                            return data

                        }

                        const data = await getData()
                        handleUnauthorized(data.message, logout);

                        if (data) {
                            setTableQueryAbortController(null)
                            setLoading(false);
                            setLoadingSearchResults(false)
                            setTotalRecords(data['queriesResults'][0].fullCount)

                            const { results } = data['queriesResults'][0];

                            // will have a check here until we deploy new UI to NM prod
                            if (process.env.REACT_APP_TRANSFORM_DATA === "true") {
                                setMappedData(results);
                            } else {
                                const transformedData = results.map((obj: any, index: number) => { return transform(obj, index) });
                                setMappedData(transformedData);
                            }

                        }

                        setRunFilters(false);

                        if (auditSearch) {
                            //addAuditEvent("Search executed", "Search params: " +  JSON.stringify(queryParams), new Date(), undefined, undefined);
                        }

                    } catch (err: any) {
                        setError(err);
                        if (err.name != "AbortError") {
                            setLoading(false);
                            setLoadingSearchResults(false)
                        }
                    }
                }

            }
            fetchData(queryDataRequest);
        }


        return () => controller.abort();
    }, [
        tableName,
        auditSearch,
        userRole,
        pageSize,
        currentPage,
        order,
        orderBy,
        searchParams,
        columnState.page["transaction-summary"]["transaction-summary"]?.filters?.length,
        pagination[TableNames.TRANSACTION_SUMMARY],
        runFilters,
        softLimit,
        userRoles
    ]);

    return { data: mappedData, runCount: runCount.current, extendViewing, error, loading, totalRecords };
};
